import imageButtonResponse from "@jspsych/plugin-image-button-response";

import { config } from "../../config/main";

import { getScreenWidth } from "../../lib/utils";
import { buildCameraStartTrial } from "../trials/camera";
import { enterFullscreenTrial } from "../trials/fullscreen";
import { holdUpMarkerTrial } from "../trials/holdUpMarker";
import { initPhotodiodeTrial } from "../trials/initPhotodiode";
import { buildTTLDeviceConnectTrial } from "../trials/ttlDevice";
import { preloadTrial, injectLayoutAssets, configureMissionPreloadAssets } from "../trials/preload";
import { buildKeyboardLayoutTrial, preloadKeyboardLayoutAvatars } from "../trials/keyboardLayout";

/**
 * Builds the block of trials needed to start and setup the experiment
 * 1) Game assets are preloaded into memory
 * 2) The experiment enters fullscreen
 * 3) The user is prompted to select their gameplay mode
 * 4) Trials used to set up a photodiode and trigger box are displayed (if applicable)
 * 5) Trials used to set up the user's camera are displayed (if applicable)
 *
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata Experiment metadata
 * @param {string} studyID The ID of the study that was just logged into
 * @param {string} participantID The ID of the participant that was just logged in
 * @param {string} sessionID The ID of the current session that was just logged in
 * @returns {Object} A jsPsych (nested) timeline object
 */
export function buildStartProcedure(jsPsych, metadata, studyID, participantID, sessionID) {
  configureMissionPreloadAssets(metadata);

  // Fire-and-forget: starts fetching the keyboard-layout-detection avatars right
  // away, well before that trial (the very first one) renders and needs them.
  preloadKeyboardLayoutAvatars();

  const procedure = [];

  // Conditionally add the TTL trigger board setup trial (WebUSB/Web Serial)
  // NOTE: Placed as early as possible, before every other setup screen - must run before
  // enterFullscreenTrial (the browser's native device picker forces an exit from fullscreen,
  // which eats the click's user-activation and breaks requestDevice()), and the more real time
  // that passes before enterFullscreenTrial, the less likely requestFullscreen() is to get
  // rejected by the browser's post-permission-prompt fullscreen cooldown.
  if (metadata.pulseTTLtrialStart) {
    procedure.push(buildTTLDeviceConnectTrial(jsPsych, metadata));
  }

  procedure.push(
    {
      ...buildKeyboardLayoutTrial(studyID, participantID, sessionID),
      on_finish: (data) => injectLayoutAssets(data.layout_config_id),
    },
    preloadTrial,
    zoomTrial1,
    zoomTrial2,
    // keyboardReminderTrial,unused after new compatibility with other keyboard layouts
    // keyboardLayoutTrial,unused
    enterFullscreenTrial,
    modeTrial
  );

  // Conditionally add the photodiode setup trials
  if (config.USE_PHOTODIODE) {
    procedure.push(holdUpMarkerTrial);
    procedure.push(initPhotodiodeTrial);
  }

  // Conditionally add the camera setup trials
  if (config.USE_CAMERA) {
    procedure.push(buildCameraStartTrial(jsPsych));
  }

  // Return the block as a nested timeline
  return { timeline: procedure };
}

/** Trial for telling the user to reset their zoom level - this is screen 1 */
const zoomTrial1 = {
  type: imageButtonResponse,
  data: { name: "setZoom" },
  stimulus: "assets/images/instructions/before_instructions_setEnvironment_1.svg",
  choices: ["Continue"],
  stimulus_width: () => getScreenWidth(0.75), // Width of the image is 75% of the screen width
};

/** Trial for telling the user to reset their zoom level - this is screen 2 */
const zoomTrial2 = {
  type: imageButtonResponse,
  data: { name: "setZoom" },
  stimulus: "assets/images/instructions/before_instructions_setEnvironment_2.svg",
  choices: ["Continue"],
  stimulus_width: () => getScreenWidth(0.75), // Width of the image is 75% of the screen width
};

/** Trial for telling the user to be mindful about keyboard */
// const keyboardReminderTrial = {
//   type: imageButtonResponse,
//   data: { name: "setKeyboard" },
//   stimulus: "assets/images/instructions/before_instructions0_Keyboard.svg",
//   choices: ["Continue"],
//   stimulus_width: () => getScreenWidth(0.75), // Width of the image is 75% of the screen width
// };

/** Trial for telling the user to pick the mode they're playing in */
const modeTrial = {
  type: imageButtonResponse,
  response_ends_trial: true,
  data: { name: "modeSelection" },
  stimulus: "assets/images/instructions/before_instructions1_mode_selection.svg",
  choices: ["Exploring", "Training", "Escaping"],
  stimulus_width: () => getScreenWidth(0.75), // Width of the image is 75% of the screen width
};
